import DropdownMenu from "@/common/components/DropdownMenu";
import Spinner from "@/common/components/Spinner";
import { cn } from "@/common/utils/cn";
import { EllipsisVerticalIcon, PencilIcon, Trash2Icon } from "lucide-react";
import {
  createElement,
  forwardRef,
  HTMLProps,
  memo,
  PropsWithChildren
} from "react";
import { DateRange } from "react-day-picker";
import { dashboardChartComponent } from "../../constants/dashboards/dashboardKind.constant";
import { useDashboardComponent } from "../../hooks/dashboards/useDashboardComponent";
import useDashboardComponentParsedFilters from "../../hooks/dashboards/useDashboardComponentParsedFilters";
import { DashboardData, DashboardFilters } from "../../types/dashboards/dashboard.type";
import { DashboardComponentData, DashboardComponentFiltersParams } from "../../types/dashboards/dashboardComponent.type";

interface DashboardComponentContentProps {
  dashboardDesiredFilters: DashboardFilters[];
  dashboardComponent: DashboardData["components"][number];
  onDelete: (id: string) => void;
  onUpdate: (data: DashboardComponentData | undefined) => void;
  filters: DashboardComponentFiltersParams & {
    date: DateRange | undefined;
  };
  isEditable: boolean;
  hasPermissionToUpdateDashboard: boolean;
}

const DashboardComponentContent = memo(({
  dashboardDesiredFilters,
  dashboardComponent,
  onDelete,
  onUpdate,
  filters,
  isEditable,
  hasPermissionToUpdateDashboard,
}: DashboardComponentContentProps) => {
  const definitionFilters = dashboardComponent.definition?.filters ?? {};

  const transformedFilters = useDashboardComponentParsedFilters({
    filters,
    dashboardDesiredFilters,
    definitionFilters,
  });

  const {
    data: dashboardComponentData,
    isPending: dashboardComponentDataIsPending,
    dataUpdatedAt: dashboardComponentDataUpdatedAt,
  } = useDashboardComponent({
    dashboardComponentId: dashboardComponent.id,
    filters: transformedFilters,
  });

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 flex flex-col gap-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col">
            <p className="text-white-400 text-sm font-semibold select-none">
              {dashboardComponent.definition?.title}
            </p>

            {dashboardComponent.definition?.subtitle && (
              <p className="text-white-300 text-xs font-semibold select-none">
                {dashboardComponent.definition?.subtitle}
              </p>
            )}
          </div>

          <div className="flex items-center gap-1">
            {isEditable && (
              <>
                <DropdownMenu
                  triggerClassName="bg-transparent hover:bg-white-100 disabled:hover:bg-transparent transition-colors px-1.5 py-1.5 -m-1.5 ml-auto"
                  triggerLabel=""
                  TriggerIcon={
                    <EllipsisVerticalIcon size={16} strokeWidth={1.5} />
                  }
                  menuPositionAlign="end"
                  disabled={!hasPermissionToUpdateDashboard}
                  tooltipMessage={!hasPermissionToUpdateDashboard ? 'Você não tem permissão' : undefined}
                  options={[
                    {
                      label: (
                        <div className="flex items-center gap-2">
                          <PencilIcon size={16} strokeWidth={1.5} />

                          <p>Editar</p>
                        </div>
                      ),
                      onClick: () => onUpdate(dashboardComponentData),
                    },
                    {
                      label: (
                        <div className="flex items-center gap-2 text-danger-soft">
                          <Trash2Icon size={16} strokeWidth={1.5} />

                          <p>Remover</p>
                        </div>
                      ),
                      onClick: () => onDelete(dashboardComponent.id),
                    },
                  ]}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className={cn(
        'flex-1 overflow-hidden',
        (dashboardComponentDataIsPending || dashboardComponentData?.data?.data?.length === 0) && 'flex items-center justify-center px-4',
      )}>
        {dashboardComponentDataIsPending ? (
          <Spinner className="fill-white-200 text-white-100" />
        ) : dashboardComponentData?.data?.data?.length === 0 ? (
          <p className="text-white-300 italic text-sm text-center">
            Não há dados para serem exibidos
          </p>
        ) : (dashboardComponentData?.data?.kind && createElement(
          dashboardChartComponent[dashboardComponentData?.data?.kind],
          {
            chartData: dashboardComponentData?.data,
            dashboardComponentUpdatedAt: dashboardComponentDataUpdatedAt,
          },
        ))}
      </div>
    </div>
  );
});

interface DashboardComponentProps extends PropsWithChildren<HTMLProps<HTMLDivElement>>, DashboardComponentContentProps { }

const DashboardComponent = forwardRef<HTMLDivElement, DashboardComponentProps>(({
  dashboardDesiredFilters,
  dashboardComponent,
  onDelete,
  onUpdate,
  children,
  filters,
  isEditable,
  hasPermissionToUpdateDashboard,
  ...rest
}, ref) => {
  return (
    <div
      ref={ref}
      {...rest}
      className={cn(
        rest.className,
        "bg-white-100 rounded-lg overflow-hidden select-none",
      )}
      style={{ ...rest.style, backdropFilter: "blur(32px)" }}
    >
      <DashboardComponentContent
        dashboardDesiredFilters={dashboardDesiredFilters}
        dashboardComponent={dashboardComponent}
        onDelete={onDelete}
        onUpdate={onUpdate}
        filters={filters}
        isEditable={isEditable}
        hasPermissionToUpdateDashboard={hasPermissionToUpdateDashboard}
      />

      {children}
    </div>
  );
},
);

export default memo(DashboardComponent);
